import { create } from 'zustand';
import {
  ApprovalRequestData,
  GroupMemberConnectionApprovalRequestData,
} from '../dto/EntityRepository';
import {
  ApprovalRequestStatus,
  ApprovalRequestType,
} from '../enum/EnumRepository';
import type { ResourceStatus } from '../util/scopedResource';

interface ApprovalRequestState {
  approvalRequestList: ApprovalRequestData[];
  listOwnerAui: string | null;
  status: ResourceStatus;
  beginApprovalRequestListFetch: (ownerAui: string) => void;
  setApprovalRequestList: (
    approvalRequestList: ApprovalRequestData[],
    ownerAui: string,
  ) => void;
  setApprovalRequestListError: () => void;
  upsertApprovalRequest: (
    approvalRequest: ApprovalRequestData,
    ownerAui: string,
  ) => void;
  removeApprovalRequest: (requestId: string) => void;
  removeGroupMemberApprovalRequests: (groupMemberId: string) => void;
  clearApprovalRequests: () => void;
}

const sortApprovalRequests = (
  approvalRequestList: ApprovalRequestData[],
): ApprovalRequestData[] =>
  [...approvalRequestList].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt));

const isSameApprovalRequestList = (
  prev: ApprovalRequestData[],
  next: ApprovalRequestData[],
): boolean =>
  prev.length === next.length
  && prev.every((request, index) => {
    const other = next[index];
    return request.id === other.id
      && request.status === other.status
      && request.version === other.version;
  });

export const useApprovalRequestStore = create<ApprovalRequestState>((set) => ({
  approvalRequestList: [],
  listOwnerAui: null,
  status: 'idle',
  beginApprovalRequestListFetch: (ownerAui) => set((state) => {
    if (
      state.listOwnerAui === ownerAui
      && state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setApprovalRequestList: (approvalRequestList, ownerAui) => set((state) => {
    const sorted = sortApprovalRequests(approvalRequestList);
    if (
      state.status === 'success'
      && state.listOwnerAui === ownerAui
      && isSameApprovalRequestList(state.approvalRequestList, sorted)
    ) {
      return state;
    }
    return {
      approvalRequestList: sorted,
      listOwnerAui: ownerAui,
      status: 'success',
    };
  }),
  setApprovalRequestListError: () => set({ status: 'error' }),
  upsertApprovalRequest: (approvalRequest, ownerAui) => set((state) => {
    const currentList = state.listOwnerAui === ownerAui
      ? state.approvalRequestList
      : [];
    return {
      approvalRequestList: sortApprovalRequests([
        ...currentList.filter((request) => request.id !== approvalRequest.id),
        approvalRequest,
      ]),
      listOwnerAui: ownerAui,
      status: 'success',
    };
  }),
  removeApprovalRequest: (requestId) => set((state) => ({
    approvalRequestList: state.approvalRequestList.filter(
      (request) => request.id !== requestId,
    ),
  })),
  removeGroupMemberApprovalRequests: (groupMemberId) => set((state) => ({
    approvalRequestList: state.approvalRequestList.filter((request) =>
      request.type !== ApprovalRequestType.GROUP_MEMBER_CONNECTION
      || String(request.detail.groupMemberId) !== groupMemberId),
  })),
  clearApprovalRequests: () => set((state) => {
    if (
      state.approvalRequestList.length === 0
      && state.listOwnerAui === null
      && state.status === 'idle'
    ) {
      return state;
    }
    return {
      approvalRequestList: [],
      listOwnerAui: null,
      status: 'idle',
    };
  }),
}));

export const selectPendingGroupMemberConnectionRequest = (
  ownerAui: string | null | undefined,
  groupMemberId: string,
) => (state: ApprovalRequestState): GroupMemberConnectionApprovalRequestData | undefined => {
  if (!ownerAui || state.listOwnerAui !== ownerAui) return undefined;
  return state.approvalRequestList.find(
    (request): request is GroupMemberConnectionApprovalRequestData =>
      request.type === ApprovalRequestType.GROUP_MEMBER_CONNECTION
      && request.status === ApprovalRequestStatus.PENDING
      && String(request.detail.groupMemberId) === groupMemberId,
  );
};
